// Questions sur la base (spec § 10 bis) : question en langage courant, réponse rédigée dont chaque chiffre vient des
// outils de la base, appelés avec les droits de l'utilisateur ; chaque fiche, société, affaire ou personne citée a son
// lien. Les réserves sont toujours affichées ; une réponse rédigée écartée par le contrôle laisse les résultats bruts.
import {
  ArrowLeft,
  BellPlus,
  History,
  ListFilter,
  Loader2,
  MessageCircleQuestion,
  Plus,
  RotateCcw,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useCreate, useLocaleState, useNotify, useTranslate } from "ra-core";
import { Link, useNavigate, useSearchParams } from "react-router";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Confirm } from "@/components/admin/confirm";

import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  answerBlocks,
  answerFromStored,
  criteriaFromAssistant,
  entityPath,
  historyGroup,
  lastFilters,
  listFilterFromAssistant,
  plainAnswer,
  type AnswerPart,
  type HistoryGroup,
  type QuestionAnswer,
  type StoredQuestion,
  type ToolCall,
} from "./questions";
import { hasCriterion } from "./savedSearchCriteria";
import { LockNote } from "./Teaser";
import { useInvited } from "./useInvited";

type Exchange = {
  question: string;
  result: QuestionAnswer | null;
  error: string | null;
};

const MAX_LENGTH = 1000;

const askBase = async (body: object): Promise<QuestionAnswer> => {
  const { data, error } =
    await getSupabaseClient().functions.invoke<QuestionAnswer>(
      "base-assistant",
      { method: "POST", body },
    );
  if (error) {
    // Le message de la fonction (limite du jour, plafond du mois) est plus utile que le statut HTTP.
    const message =
      error instanceof FunctionsHttpError
        ? (await error.context.json().catch(() => null))?.message
        : null;
    throw new Error(message ?? error.message);
  }
  return data as QuestionAnswer;
};

// Invité (spec § 3, aperçu de la base) : 3 questions sur 7 jours, en comptes seulement ; la fonction le vérifie.
const INVITED_QUESTIONS = 3;

/** Questions restantes sur 7 jours pour un invité ; null pour les autres. */
const useInvitedQuota = () => {
  const invited = useInvited();
  const { data: asked, refetch } = useQuery({
    queryKey: ["invited_questions_week"],
    enabled: invited,
    queryFn: async () => {
      const { count, error } = await getSupabaseClient()
        .from("assistant_questions")
        .select("id", { count: "exact", head: true })
        .gte("created_at", new Date(Date.now() - 7 * 86_400_000).toISOString());
      if (error) throw error;
      return count ?? 0;
    },
  });
  return {
    invited,
    remaining:
      invited && asked != null ? Math.max(0, INVITED_QUESTIONS - asked) : null,
    refetch,
  };
};

const useT = () => {
  const translate = useTranslate();
  return (key: string, options?: object) =>
    translate(`resources.projects.questions.${key}`, options);
};

/** Barre « Poser une question » : ouvre la page des questions avec la question saisie. */
export const AskBar = () => {
  const t = useT();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [text, setText] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const question = text.trim();
    if (question) navigate(`/questions?q=${encodeURIComponent(question)}`);
  };
  return (
    <form onSubmit={submit} className="flex gap-2">
      <div className="relative flex-1 min-w-0">
        <MessageCircleQuestion className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          className="pl-8"
          value={text}
          maxLength={MAX_LENGTH}
          onChange={(e) => setText(e.target.value)}
          // Sur téléphone, l'exemple complet est coupé : une invite courte à la place.
          placeholder={t(isMobile ? "placeholder_short" : "placeholder")}
          aria-label={t("ask")}
        />
      </div>
      <Button type="submit" disabled={!text.trim()}>
        {t("ask")}
      </Button>
      {/* Les conversations passées, sans avoir à poser une question d'abord. */}
      <Button
        asChild
        variant="outline"
        size={isMobile ? "icon" : "default"}
        aria-label={t("history.title")}
        title={t("history.title")}
      >
        <Link to="/questions?h=1">
          <History className="size-4" />
          {!isMobile && t("history.title")}
        </Link>
      </Button>
    </form>
  );
};

/** Accès à la page des questions depuis l'en-tête de la liste sur téléphone. */
export const QuestionsLink = () => {
  const t = useT();
  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className="rounded-full"
      aria-label={t("title")}
    >
      <Link to="/questions">
        <MessageCircleQuestion className="size-5" />
      </Link>
    </Button>
  );
};

/** Bouton de la barre d'outils de la liste sur ordinateur. */
export const QuestionsButton = () => {
  const t = useT();
  return (
    <Button asChild variant="outline" size="sm">
      <Link to="/questions">
        <MessageCircleQuestion className="size-4" />
        {t("ask_question")}
      </Link>
    </Button>
  );
};

type Conversation = { id: number; title: string; updated_at: string };

const STORED_COLUMNS =
  "id, question, interpretation, answer, caveats, entities, tool_calls, refused, discarded, rating, model, created_at";

/** Conversations de l'utilisateur, les plus récentes d'abord (la base ne rend que les siennes). */
const useConversations = () => {
  const t = useT();
  const notify = useNotify();
  const [conversations, setConversations] = useState<Conversation[] | null>(
    null,
  );
  const refresh = useCallback(async () => {
    const { data, error } = await getSupabaseClient()
      .from("assistant_conversations")
      .select("id, title, updated_at")
      .order("updated_at", { ascending: false })
      .limit(200);
    if (error) {
      notify(t("history.load_error", { message: error.message }), {
        type: "error",
      });
      return;
    }
    setConversations(data as Conversation[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return { conversations, refresh };
};

export const QuestionsPage = () => {
  const t = useT();
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const [locale = "fr"] = useLocaleState();
  const [params, setParams] = useSearchParams();
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  // Sur téléphone, l'historique remplace la conversation tant qu'il est ouvert.
  // Ouvert d'emblée quand on vient du bouton « Historique » de l'accueil (?h=1).
  const [showHistory, setShowHistory] = useState(params.get("h") === "1");
  const end = useRef<HTMLDivElement>(null);
  const started = useRef<string | null>(null);
  const { conversations, refresh } = useConversations();
  const quota = useInvitedQuota();
  const exhausted = quota.remaining === 0;

  const ask = async (
    question: string,
    previous: Exchange[],
    conversation: number | null,
  ) => {
    setBusy(true);
    setExchanges([...previous, { question, result: null, error: null }]);
    // Les trois échanges précédents, pour une fonction qui ne lirait pas encore l'historique enregistré.
    const history = previous
      .filter((e) => e.result?.answer)
      .slice(-3)
      .map((e) => ({
        question: e.question,
        answer: plainAnswer(e.result!.answer!, e.result!.entities),
      }));
    let done: Exchange;
    try {
      const result = await askBase({
        question,
        history,
        language: locale,
        ...(conversation != null ? { conversation_id: conversation } : {}),
      });
      done = { question, result, error: null };
      if (result.conversation_id != null && conversation == null) {
        setConversationId(result.conversation_id);
        setParams({ c: String(result.conversation_id) }, { replace: true });
      }
      refresh();
    } catch (error) {
      done = { question, result: null, error: (error as Error).message };
    }
    setExchanges([...previous, done]);
    setBusy(false);
    if (quota.invited) quota.refetch();
  };

  const open = async (id: number) => {
    setShowHistory(false);
    if (id === conversationId) return;
    setLoading(true);
    setConversationId(id);
    setExchanges([]);
    const { data, error } = await getSupabaseClient()
      .from("assistant_questions")
      .select(STORED_COLUMNS)
      .eq("conversation_id", id)
      .order("id");
    setLoading(false);
    if (error || !data?.length) {
      notify(
        error
          ? t("history.load_error", { message: error.message })
          : t("history.not_found"),
        { type: "error" },
      );
      setConversationId(null);
      setParams({}, { replace: true });
      return;
    }
    setExchanges(
      (data as StoredQuestion[]).map((row) => ({
        question: row.question,
        result: answerFromStored(row),
        error: null,
      })),
    );
  };

  const startNew = () => {
    setShowHistory(false);
    setConversationId(null);
    setExchanges([]);
    setText("");
    setParams({}, { replace: true });
  };

  // Question venue de la barre du tableau de bord ou de la liste : une conversation nouvelle.
  // Conversation rouverte par son lien (?c=…).
  useEffect(() => {
    const question = params.get("q")?.trim();
    if (question && started.current !== question) {
      started.current = question;
      setParams({}, { replace: true });
      setConversationId(null);
      ask(question, [], null);
      return;
    }
    const id = Number(params.get("c"));
    if (id && id !== conversationId && !busy) open(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [exchanges]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const question = text.trim();
    if (!question || busy) return;
    setText("");
    ask(question, exchanges, conversationId);
  };

  const removed = (id: number) => {
    if (id === conversationId) startNew();
    refresh();
  };

  const history = (
    <HistoryList
      conversations={conversations}
      current={conversationId}
      disabled={busy}
      onOpen={(id) => {
        setShowHistory(false);
        setParams({ c: String(id) });
      }}
      onNew={startNew}
      onDeleted={removed}
    />
  );

  if (isMobile && showHistory) {
    return (
      <div className="mt-2 pb-6 flex flex-col gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="self-start -ml-2"
          onClick={() => setShowHistory(false)}
        >
          <ArrowLeft className="size-4" />
          {t("history.back")}
        </Button>
        {history}
      </div>
    );
  }

  return (
    <div className="mt-2 pb-6 flex gap-6">
      {!isMobile && (
        <aside className="w-64 shrink-0">
          <div className="sticky top-2 max-h-[calc(100vh-6rem)] overflow-y-auto">
            {history}
          </div>
        </aside>
      )}
      <div className="flex flex-col gap-4 max-w-3xl min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold">{t("title")}</h2>
            {!quota.invited && (
              <p className="text-sm text-muted-foreground">{t("intro")}</p>
            )}
            {quota.remaining != null && !exhausted && (
              <p className="mt-1 text-sm text-muted-foreground">
                {translate("resources.projects.teaser.questions_counts", {
                  smart_count: quota.remaining,
                })}
              </p>
            )}
          </div>
          {isMobile && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setShowHistory(true)}
            >
              <History className="size-4" />
              {t("history.title")}
            </Button>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        )}

        {!exchanges.length && !loading && (
          <div className="flex flex-wrap gap-2">
            {(
              ["example_1", "example_2", "example_3", "example_4"] as const
            ).map((key) => (
              <Button
                key={key}
                variant="outline"
                size="sm"
                className="h-auto max-w-full py-1.5 whitespace-normal text-left justify-start"
                disabled={busy || exhausted}
                onClick={() => ask(t(key), [], null)}
              >
                {t(key)}
              </Button>
            ))}
          </div>
        )}

        {exchanges.map((exchange, index) => (
          <ExchangeView
            key={index}
            exchange={exchange}
            onRepeat={
              busy
                ? undefined
                : () => ask(exchange.question, exchanges, conversationId)
            }
          />
        ))}
        <div ref={end} />

        {quota.invited && (exhausted || exchanges.length > 0) && (
          <LockNote>
            {translate(
              exhausted
                ? "resources.projects.teaser.questions_none"
                : "resources.projects.teaser.questions_lock",
            )}
          </LockNote>
        )}
        <form onSubmit={submit} className="flex gap-2 md:sticky md:bottom-2">
          <Input
            className="flex-1 min-w-0 bg-background"
            value={text}
            maxLength={MAX_LENGTH}
            disabled={busy || loading || exhausted}
            onChange={(e) => setText(e.target.value)}
            placeholder={t(
              exchanges.length ? "follow_up_placeholder" : "placeholder",
            )}
            aria-label={t("ask")}
          />
          <Button
            type="submit"
            disabled={busy || loading || exhausted || !text.trim()}
            aria-label={t("ask")}
          >
            {busy ? <Loader2 className="animate-spin" /> : <Send />}
          </Button>
        </form>
      </div>
    </div>
  );
};

const GROUPS: HistoryGroup[] = ["today", "yesterday", "week", "older"];

/** Conversations de l'utilisateur, rangées par jour de la dernière question ; chacune s'efface. */
const HistoryList = ({
  conversations,
  current,
  disabled,
  onOpen,
  onNew,
  onDeleted,
}: {
  conversations: Conversation[] | null;
  current: number | null;
  disabled: boolean;
  onOpen: (id: number) => void;
  onNew: () => void;
  onDeleted: (id: number) => void;
}) => {
  const t = useT();
  const notify = useNotify();
  const [toDelete, setToDelete] = useState<Conversation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const now = new Date();
  const grouped = GROUPS.map((group) => ({
    group,
    items: (conversations ?? []).filter(
      (c) => historyGroup(c.updated_at, now) === group,
    ),
  })).filter((g) => g.items.length);

  const remove = async () => {
    if (!toDelete) return;
    setDeleting(true);
    const { error } = await getSupabaseClient().rpc(
      "delete_assistant_conversation",
      { conversation: toDelete.id },
    );
    setDeleting(false);
    if (error) notify(error.message, { type: "error" });
    else {
      notify(t("history.deleted"), { type: "success" });
      onDeleted(toDelete.id);
    }
    setToDelete(null);
  };

  return (
    <nav aria-label={t("history.title")} className="flex flex-col gap-3">
      <Button
        variant="outline"
        size="sm"
        className="justify-start"
        disabled={disabled}
        onClick={onNew}
      >
        <Plus className="size-4" />
        {t("history.new")}
      </Button>
      {conversations && !conversations.length && (
        <p className="text-xs text-muted-foreground">{t("history.empty")}</p>
      )}
      {grouped.map(({ group, items }) => (
        <div key={group} className="flex flex-col gap-0.5">
          <div className="px-2 pb-1 text-xs font-medium text-muted-foreground">
            {t(`history.${group}`)}
          </div>
          {items.map((conversation) => (
            <div
              key={conversation.id}
              className={`group flex items-center rounded-[3px] ${
                conversation.id === current ? "bg-muted" : "hover:bg-muted/60"
              }`}
            >
              <button
                type="button"
                disabled={disabled}
                aria-current={conversation.id === current ? "page" : undefined}
                onClick={() => onOpen(conversation.id)}
                className="flex-1 min-w-0 truncate px-2 py-2 md:py-1.5 text-left text-sm"
                title={conversation.title}
              >
                {conversation.title}
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-muted-foreground md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
                aria-label={t("history.delete")}
                title={t("history.delete")}
                disabled={disabled}
                onClick={() => setToDelete(conversation)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      ))}
      <Confirm
        isOpen={!!toDelete}
        loading={deleting}
        title={t("history.delete_title")}
        content={t("history.delete_content")}
        confirm={t("history.delete")}
        onConfirm={remove}
        onClose={() => setToDelete(null)}
      />
    </nav>
  );
};

const ExchangeView = ({
  exchange,
  onRepeat,
}: {
  exchange: Exchange;
  onRepeat?: () => void;
}) => {
  const t = useT();
  const { question, result, error } = exchange;
  return (
    <div className="flex flex-col gap-2">
      <div className="self-end max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm">
        {question}
      </div>
      {!result && !error && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("working")}
        </div>
      )}
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {result && (
        <AnswerView question={question} result={result} onRepeat={onRepeat} />
      )}
    </div>
  );
};

const AnswerView = ({
  question,
  result,
  onRepeat,
}: {
  question: string;
  result: QuestionAnswer;
  onRepeat?: () => void;
}) => {
  const t = useT();
  const [locale = "fr"] = useLocaleState();
  const filters = result.refused ? null : lastFilters(result.results);
  return (
    <Card className="py-4">
      <CardContent className="flex flex-col gap-3 px-4">
        {result.asked_at && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>
              {t("history.asked_on", {
                date: new Date(result.asked_at).toLocaleDateString(locale, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }),
              })}
            </span>
            {onRepeat && (
              <button
                type="button"
                onClick={onRepeat}
                className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground"
              >
                <RotateCcw className="size-3" />
                {t("history.repeat")}
              </button>
            )}
          </div>
        )}
        {result.interpretation && (
          <p className="text-xs text-muted-foreground">
            {t("understood", { interpretation: result.interpretation })}
          </p>
        )}

        {result.discarded ? (
          <div className="flex gap-2 text-sm text-inferred-ink">
            <TriangleAlert className="size-4 shrink-0 mt-0.5" />
            {t("discarded")}
          </div>
        ) : (
          result.answer && <AnswerText result={result} />
        )}

        {!!result.caveats.length && (
          <ul className="flex flex-col gap-1 text-xs text-inferred-ink">
            {result.caveats.map((caveat, index) => (
              <li key={index}>{caveat}</li>
            ))}
          </ul>
        )}

        {(result.discarded || result.results.some((r) => r.rows?.length)) && (
          <ToolResults calls={result.results} open={result.discarded} />
        )}

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          {filters && <ListButton filters={filters} />}
          {filters && <WatchButton question={question} filters={filters} />}
          <div className="flex-1" />
          {result.question_id != null && (
            <Rating
              questionId={result.question_id}
              initial={result.rating ?? null}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AnswerText = ({ result }: { result: QuestionAnswer }) => {
  const blocks = answerBlocks(result.answer ?? "", result.entities);
  return (
    <div className="flex flex-col gap-2 text-sm leading-relaxed">
      {blocks.map((block, index) =>
        block.type === "paragraph" ? (
          <p key={index}>
            <Parts parts={block.parts} />
          </p>
        ) : (
          <ul key={index} className="list-disc pl-5 flex flex-col gap-1">
            {block.items.map((item, i) => (
              <li key={i}>
                <Parts parts={item} />
              </li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
};

const Parts = ({ parts }: { parts: AnswerPart[] }) => (
  <>
    {parts.map((part, index) => {
      if ("text" in part) return <span key={index}>{part.text}</span>;
      const path = entityPath(part.entity);
      const label = part.entity.label ?? part.entity.key;
      return path ? (
        <Link
          key={index}
          to={path}
          className="font-medium underline underline-offset-2"
        >
          {label}
        </Link>
      ) : (
        <span key={index}>{label}</span>
      );
    })}
  </>
);

// Colonnes montrées dans les résultats bruts : les identifiants servent aux liens, pas à la lecture.
const HIDDEN = /(^id$|_id$|_ids$|^total)/;

const cell = (value: unknown) => {
  if (value === null || value === undefined) return "–";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/** Résultats des outils, tels que la base les a rendus : toujours consultables, ouverts si la réponse est écartée. */
const ToolResults = ({ calls, open }: { calls: ToolCall[]; open: boolean }) => {
  const t = useT();
  const shown = calls.filter((c) => c.rows?.length || c.error);
  return (
    <details className="text-xs" open={open}>
      <summary className="cursor-pointer text-muted-foreground">
        {t("results", { smart_count: shown.length })}
      </summary>
      <div className="mt-2 flex flex-col gap-3">
        {shown.map((call, index) => {
          const rows = call.rows ?? [];
          const columns = Object.keys(rows[0] ?? {}).filter(
            (c) => !HIDDEN.test(c),
          );
          return (
            <div key={index} className="flex flex-col gap-1">
              <div className="font-medium">
                {t(`tools.${call.name}`, { _: call.name })}
                {call.error && (
                  <span className="text-destructive"> · {call.error}</span>
                )}
              </div>
              {!!rows.length && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        {columns.map((column) => (
                          <th
                            key={column}
                            className="pr-3 py-1 font-normal whitespace-nowrap"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 20).map((row, r) => (
                        <tr key={r} className="border-t align-top">
                          {columns.map((column) => (
                            <td key={column} className="pr-3 py-1">
                              <RowCell row={row} column={column} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
};

// Le nom d'une fiche des résultats ouvre la fiche.
const RowCell = ({
  row,
  column,
}: {
  row: Record<string, unknown>;
  column: string;
}) => {
  const projectId = (row.project_id ??
    (row.aegyra_project_id ? row.id : null)) as string | null;
  const isName = column === "name" || column === "project_name";
  if (isName && projectId && row.aegyra_project_id) {
    return (
      <Link
        to={`/projects/${projectId}/show`}
        className="underline underline-offset-2"
      >
        {cell(row[column])}
      </Link>
    );
  }
  return <>{cell(row[column])}</>;
};

/** Ouvre la liste des projets avec les filtres de la réponse ; dit ceux que la liste ne sait pas appliquer. */
const ListButton = ({ filters }: { filters: Record<string, unknown> }) => {
  const t = useT();
  const { filter, missing } = listFilterFromAssistant(filters);
  return (
    <Button
      asChild
      variant="outline"
      size="sm"
      title={
        missing.length
          ? t("missing_list", { keys: missingLabels(t, missing) })
          : undefined
      }
    >
      <Link
        to={`/projects?filter=${encodeURIComponent(JSON.stringify(filter))}`}
      >
        <ListFilter className="size-4" />
        {t(missing.length ? "see_in_list_partial" : "see_in_list")}
      </Link>
    </Button>
  );
};

const missingLabels = (t: ReturnType<typeof useT>, keys: string[]) =>
  keys.map((key) => t(`filter_keys.${key}`, { _: key })).join(", ");

/** Enregistre les critères de la réponse comme veille (alerte chaque nuit sur les fiches nouvelles). */
const WatchButton = ({
  question,
  filters,
}: {
  question: string;
  filters: Record<string, unknown>;
}) => {
  const t = useT();
  const notify = useNotify();
  const [create, { isPending }] = useCreate();
  const [saved, setSaved] = useState(false);
  const { criteria, missing } = criteriaFromAssistant(filters);
  if (!hasCriterion(criteria)) return null;
  const save = () =>
    create(
      "saved_searches",
      { data: { name: question.slice(0, 120), ...criteria } },
      {
        onSuccess: () => {
          setSaved(true);
          notify(
            missing.length
              ? t("watch_saved_partial", { keys: missingLabels(t, missing) })
              : t("watch_saved"),
            { type: "success" },
          );
        },
        onError: (error) =>
          notify(error instanceof Error ? error.message : String(error), {
            type: "error",
          }),
      },
    );
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isPending || saved}
      onClick={save}
      title={
        missing.length
          ? t("missing_watch", { keys: missingLabels(t, missing) })
          : undefined
      }
    >
      <BellPlus className="size-4" />
      {t(saved ? "watch_done" : "watch")}
    </Button>
  );
};

/** Pouce levé ou baissé, noté au journal des questions (lu par les administrateurs). */
const Rating = ({
  questionId,
  initial,
}: {
  questionId: number;
  initial: 1 | -1 | null;
}) => {
  const t = useT();
  const notify = useNotify();
  const [value, setValue] = useState<1 | -1 | null>(initial);
  const rate = async (next: 1 | -1) => {
    const { error } = await getSupabaseClient().rpc("rate_assistant_question", {
      question: questionId,
      value: next,
    });
    if (error) notify(error.message, { type: "error" });
    else setValue(next);
  };
  return (
    <div className="flex items-center gap-1">
      <Button
        variant={value === 1 ? "secondary" : "ghost"}
        size="icon"
        className="size-8"
        aria-label={t("useful")}
        aria-pressed={value === 1}
        onClick={() => rate(1)}
      >
        <ThumbsUp className="size-4" />
      </Button>
      <Button
        variant={value === -1 ? "secondary" : "ghost"}
        size="icon"
        className="size-8"
        aria-label={t("not_useful")}
        aria-pressed={value === -1}
        onClick={() => rate(-1)}
      >
        <ThumbsDown className="size-4" />
      </Button>
    </div>
  );
};

QuestionsPage.path = "/questions";
